import { hasTiptapText, isEmptyTiptap } from "@/util/tiptap";
import { MAX_COMMENT_FILE_SIZE_BYTES, MAX_COMMENT_FILE_SIZE_MB } from "./constants";
import type { CommentFileUpload, OversizeFile, TiptapDoc, TodoComment } from "./types";

export const formatFileSize = (bytes?: number): string => {
  if (bytes == null) {
    return "";
  }

  const KB = 1024;
  const MB = KB * 1024;

  if (bytes < KB) {
    return `${bytes} B`;
  }
  if (bytes < MB) {
    return `${(bytes / KB).toFixed(0)} KB`;
  }
  return `${(bytes / MB).toFixed(1)} MB`;
};

export const getInitials = (fullName: string | undefined): string => {
  const words = (fullName ?? "").trim().split(/\s+/).filter(Boolean);
  const first = words[0]?.[0] ?? "";
  const last = words.length > 1 ? (words[words.length - 1]?.[0] ?? "") : "";
  return `${first}${last}`.toUpperCase();
};

export const getAttachmentName = (upload: CommentFileUpload): string =>
  upload.original_file_name ?? upload.file_name ?? upload.key;

export const isOversize = (file: { size: number }): boolean => file.size > MAX_COMMENT_FILE_SIZE_BYTES;

export const getOversizeMessage = (files: OversizeFile[]): string | undefined => {
  if (files.length === 1) {
    const [file] = files;
    return `“${file.name}” is ${formatFileSize(file.size)}. Each file must be ${MAX_COMMENT_FILE_SIZE_MB} MB or smaller.`;
  }
  if (files.length > 1) {
    const names = files.map((file) => file.name).join(", ");
    return `${files.length} files are over the ${MAX_COMMENT_FILE_SIZE_MB} MB limit and weren’t attached: ${names}.`;
  }
  return undefined;
};

export const isCommentEdited = ({ created_at, updated_at }: Pick<TodoComment, "created_at" | "updated_at">): boolean =>
  !!updated_at && updated_at !== created_at;

export const hasCommentBody = (body: TiptapDoc | undefined): body is TiptapDoc =>
  !isEmptyTiptap(body) && hasTiptapText(body);

export const toCommentBodyPayload = (body: TiptapDoc | undefined): TiptapDoc => (hasCommentBody(body) ? body : {});

export const sortCommentsAscending = (comments: TodoComment[]): TodoComment[] =>
  [...comments].sort((a, b) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime());
